// Cut-edge computation for marching-ants borders (TV1-1690 / B-5a).
//
// Replaces the heavyweight `selectCutBorderMap` selector that every visible
// Cell used to subscribe to. The whole-grid border map rebuilt on every cut
// state, visualRows, or columns change — with ~1500 visible cells, that was
// a re-render storm even when most cells weren't on the cut boundary.
//
// New approach: DataRow (one subscription per visible row) reads a single
// `Set<cellKey>` (`selectCutCellKeySet`) plus the visualRows + visibleColumns
// it already needs, and feeds each Cell a precomputed `CutEdges | null` prop.
// Cells outside the cut set get `null` and pay nothing.

export type CutEdges = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};

// Returns the marching-ants edge flags for a cell whose cellKey already
// resolved to "in the cut set". Each edge is `true` when the neighbor in
// that direction is NOT in the cut set (i.e. the cell is on the cut
// boundary). When a neighbor key is `null` (the cell is at the grid edge),
// the edge is also `true` — the boundary of the grid is always a boundary
// of the cut region.
//
// Returns `null` when the current cell is not in the cut set: no marching
// ants and the caller can skip the four overlay divs entirely.
export const computeCutEdges = (
  cellKey: string,
  cutCellKeySet: Set<string>,
  rowAboveKey: string | null,
  rowBelowKey: string | null,
  colLeftKey: string | null,
  colRightKey: string | null
): CutEdges | null => {
  if ( !cutCellKeySet.has( cellKey )) return null;
  return {
    top: !rowAboveKey || !cutCellKeySet.has( rowAboveKey ),
    bottom: !rowBelowKey || !cutCellKeySet.has( rowBelowKey ),
    left: !colLeftKey || !cutCellKeySet.has( colLeftKey ),
    right: !colRightKey || !cutCellKeySet.has( colRightKey )
  };
};
